import React, { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { TEXT_TEMPLATE_VARIABLES, formatVariableToken } from '@/lib/textTemplateVariables'

/**
 * Compact variable picker for inserting template tokens into text fields.
 * Uses an in-place dropdown (not portalled) so scrolling works inside modals.
 */
export default function VariableInsertButton({
  onInsert,
  variables = TEXT_TEMPLATE_VARIABLES,
  legacy = false,
  buttonLabel = 'Insert Variable',
  className = '',
  disabled = false,
  variant = 'outline',
}) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined

    const handlePointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const grouped = variables.reduce((acc, variable) => {
    const group = variable.custom ? 'custom' : (variable.group || 'general')
    if (!acc[group]) acc[group] = []
    acc[group].push(variable)
    return acc
  }, {})

  const groupOrder = ['custom', ...Object.keys(grouped).filter((g) => g !== 'custom')]
  const groupLabels = { custom: 'Template Variables' }

  const handleSelect = (variable) => {
    const token = variable.insertToken || formatVariableToken(variable.key, legacy)
    onInsert(token)
    setOpen(false)
  }

  const buttonClassName =
    variant === 'primary'
      ? 'bg-blue-500 hover:bg-blue-600 text-white border-blue-500 hover:border-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 dark:border-blue-600'
      : ''

  return (
    <div className={`relative inline-block ${className}`} ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled}
        className={buttonClassName}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Plus className="h-3 w-3 mr-1" />
        {buttonLabel}
      </Button>
      {open && (
        <div className="absolute right-0 z-[200] mt-1 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
          <Command className="bg-popover">
            <CommandInput
              placeholder="Search variables..."
              className="rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
            <CommandList className="max-h-64 overflow-y-auto overscroll-contain">
              <CommandEmpty>No variables found.</CommandEmpty>
              {groupOrder.filter((group) => grouped[group]?.length).map((group) => (
                <CommandGroup
                  key={group}
                  heading={groupLabels[group] || group}
                >
                  {grouped[group].map((variable) => (
                    <CommandItem
                      key={variable.key}
                      value={`${variable.label} ${variable.key} ${group}`}
                      onSelect={() => handleSelect(variable)}
                      className="flex cursor-pointer flex-col items-start gap-0.5 py-2"
                    >
                      <span className="font-medium">
                        {variable.label}
                        {variable.custom && (
                          <span className="ml-1 text-[10px] uppercase text-muted-foreground">custom</span>
                        )}
                      </span>
                      <span className="break-all font-mono text-xs text-muted-foreground">
                        {formatVariableToken(variable.key, legacy)}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </div>
      )}
    </div>
  )
}
